// Piezas comunes de los modelos de actuadores: placas con serigrafía, conectores, borneras, puentes,
// LEDs, chips y cables.
import * as THREE from 'three';
import { M, mat, canvas, texture, text, extrudeBoard, boardShape, glow, setGlow, emissiveMat, setEmissive, cachedGeo } from '../kit.js';

export const PCB_T = 1.6;
export const P = 2.54;
export const SILK = '#f2f4f1';

/** Geometría compartida entre instancias (no se libera al borrar una pieza). */
export function sharedGeo(key, make) {
  return cachedGeo('act-' + key, () => {
    const g = make();
    g.userData.shared = true;
    return g;
  });
}

const texCache = new Map();
/** Textura dibujada una sola vez y compartida. draw(g, X, Y) recibe funciones de mm a lienzo (origen al centro). */
export function sharedTexture(key, w, d, draw, s = 12) {
  let t = texCache.get(key);
  if (!t) {
    const { c, g } = canvas(w, d, s);
    draw(g, (x) => x + w / 2, (z) => z + d / 2);
    t = texture(c);
    t.userData.shared = true;
    texCache.set(key, t);
  }
  return t;
}

/** Texto de serigrafía con los valores usuales. */
export function silk(g, str, x, y, opts = {}) {
  text(g, str, x, y, { size: 1.15, color: SILK, weight: 700, ...opts });
}

/** Isla de cobre estañado (para pines y tornillos) dibujada en la serigrafía. */
export function pad(g, x, y, r = 0.9, square = false) {
  g.fillStyle = '#d6c08a';
  if (square) g.fillRect(x - r, y - r, 2 * r, 2 * r);
  else {
    g.beginPath();
    g.arc(x, y, r, 0, Math.PI * 2);
    g.fill();
  }
}

/** Pistas tenues bajo la máscara. */
export function traces(g, color, paths, width = 0.35) {
  g.strokeStyle = color;
  g.lineWidth = width;
  g.lineCap = 'round';
  g.lineJoin = 'round';
  for (const pts of paths) {
    g.beginPath();
    g.moveTo(pts[0][0], pts[0][1]);
    for (const [x, y] of pts.slice(1)) g.lineTo(x, y);
    g.stroke();
  }
}

/** Placa de circuito con serigrafía; y0 = cara inferior. Los agujeros llevan su anillo de estaño. */
export function pcb(root, { key, w, d, y0, color, edge, draw, holes = [], r = 1, s = 12 }) {
  const tex = sharedTexture(key, w, d, (g, X, Y) => {
    g.fillStyle = color;
    g.fillRect(0, 0, w, d);
    if (draw) draw(g, X, Y);
  }, s);
  const mesh = extrudeBoard(boardShape(w, d, { r }), PCB_T, tex, edge, { holes: holes.map((h) => [...h]), w, d, y0 });
  root.add(mesh);
  return mesh;
}

export function holeRings(b, holes, y) {
  for (const [x, z, r] of holes) b.add(new THREE.RingGeometry(r, r + 1, 20).rotateX(-Math.PI / 2), M.tin, { x, y: y + 0.02, z });
}

/** Conector macho recto (base negra de 2.5 mm y pin de 6 mm); y0 = cara superior del PCB. */
export function maleHeader(b, pts, y0) {
  for (const [x, z] of pts) {
    b.box(2.5, 2.5, 2.5, M.blackPlastic, x, y0, z);
    b.box(0.64, 11, 0.64, M.gold, x, y0 - 2.5, z);
  }
}

/** Puente (jumper) negro sobre dos pines vecinos (a y b: [x, z]). */
export function jumperCap(b, a, c, y0) {
  const along = Math.abs(a[0] - c[0]) > Math.abs(a[1] - c[1]) ? 'x' : 'z';
  const cx = (a[0] + c[0]) / 2;
  const cz = (a[1] + c[1]) / 2;
  const [w, d] = along === 'x' ? [5.1, 2.6] : [2.6, 5.1];
  b.box(w, 5.6, d, mat('#151517', { roughness: 0.5 }), cx, y0 + 2.6, cz);
  b.box(along === 'x' ? 3.4 : 1.2, 0.2, along === 'x' ? 1.2 : 3.4, M.hole, cx, y0 + 8.2, cz);
}

/**
 * Bornera de tornillo (paso de 5 mm). face: lado por donde entran los cables ('-x', '+x', '+z' o '-z').
 * La entrada queda a 3.6 mm sobre el PCB, igual que los pines de la definición.
 */
export function screwTerminal(b, cx, cz, n, y0, face, color = '#2d8f4e') {
  const pitch = 5;
  const depth = 7.6;
  const along = face === '+z' || face === '-z' ? 'x' : 'z';
  const sgn = face[0] === '-' ? -1 : 1;
  const len = n * pitch;
  const body = mat(color, { roughness: 0.55 });
  const dark = mat('#0d0e10', { roughness: 0.9 });
  const box = (w, h, d, m, x, y, z) => (along === 'x' ? b.box(w, h, d, m, x, y, z) : b.box(d, h, w, m, z, y, x));
  // Coordenadas locales: u a lo largo de la fila, v hacia la cara de entrada.
  const U = along === 'x' ? cx : cz;
  const V = along === 'x' ? cz : cx;
  box(len, 7.2, depth, body, U, y0, V);
  box(len, 2.8, depth * 0.62, body, U, y0 + 7.2, V - sgn * depth * 0.19);
  for (let i = 0; i < n; i++) {
    const u = U + (i - (n - 1) / 2) * pitch;
    // Entrada del cable con su prensa metálica
    box(3.4, 3.4, 0.4, dark, u, y0 + 1.9, V + sgn * (depth / 2 - 0.15));
    box(2.6, 0.6, 0.5, M.tin, u, y0 + 1.9, V + sgn * (depth / 2 - 0.1));
    // Tornillo arriba
    const sx = along === 'x' ? u : V - sgn * depth * 0.19;
    const sz = along === 'x' ? V - sgn * depth * 0.19 : u;
    b.cyl(1.7, 1.7, 0.3, dark, sx, y0 + 9.75, sz, 18);
    b.cyl(1.45, 1.45, 0.45, M.steel, sx, y0 + 9.6, sz, 18);
    b.box(along === 'x' ? 0.4 : 2.6, 0.12, along === 'x' ? 2.6 : 0.4, dark, sx, y0 + 10.0, sz, { ry: 0.6 });
    // Pines de la bornera por debajo de la placa
    b.box(1, 3, 1, M.tin, along === 'x' ? u : V, y0 - 3.2, along === 'x' ? V : u);
  }
}

/** LED SMD 0805 con halo; set(b) con brillo de 0 a 1.5. */
export function smdLed(root, x, y, z, color, rot = 0) {
  const off = '#efe9dc';
  const m = emissiveMat(off, color, { roughness: 0.3 });
  const mesh = new THREE.Mesh(sharedGeo('smdled', () => new THREE.BoxGeometry(2.0, 0.75, 1.25)), m);
  mesh.position.set(x, y + 0.375, z);
  mesh.rotation.y = rot;
  root.add(mesh);
  const halo = glow(color, 7);
  halo.position.set(x, y + 1.3, z);
  root.add(halo);
  return {
    set(b) {
      setEmissive(m, b, 14);
      m.color.set(b > 0.05 ? color : off);
      setGlow(halo, b * 0.85);
    },
  };
}

/** LED de 3 mm con patas en el PCB (y0 = cara superior). */
export function led3mm(root, b, x, y0, z, color) {
  const c = new THREE.Color(color);
  const m = new THREE.MeshStandardMaterial({ color: c.clone().multiplyScalar(0.55), emissive: c, emissiveIntensity: 0, roughness: 0.15, transparent: true, opacity: 0.85 });
  const dome = new THREE.Mesh(sharedGeo('led3', () => {
    const pts = [new THREE.Vector2(0, 0), new THREE.Vector2(1.9, 0), new THREE.Vector2(1.9, 0.6), new THREE.Vector2(1.5, 0.6), new THREE.Vector2(1.5, 3.2)];
    for (let i = 1; i <= 8; i++) {
      const a = (i / 8) * (Math.PI / 2);
      pts.push(new THREE.Vector2(1.5 * Math.cos(a), 3.2 + 1.5 * Math.sin(a)));
    }
    return new THREE.LatheGeometry(pts, 20);
  }), m);
  dome.position.set(x, y0 + 0.8, z);
  dome.castShadow = true;
  root.add(dome);
  for (const dx of [-0.6, 0.6]) b.box(0.4, 0.8, 0.4, M.tin, x + dx, y0, z);
  const halo = glow(color, 10);
  halo.position.set(x, y0 + 4.2, z);
  root.add(halo);
  return {
    set(v) {
      m.emissiveIntensity = v * 15;
      m.opacity = 0.85 + Math.min(0.15, v * 0.2);
      setGlow(halo, v);
    },
  };
}

const labelCache = new Map();
/** Rótulo plano (texto claro sobre transparente o sobre un color) para la cara de un chip o módulo. */
export function labelPlane(w, d, key, draw, { s = 24, transparent = true } = {}) {
  let t = labelCache.get(key);
  if (!t) {
    const { c, g } = canvas(w, d, s);
    draw(g, w, d);
    t = texture(c);
    t.userData.shared = true;
    labelCache.set(key, t);
  }
  const m = new THREE.Mesh(new THREE.PlaneGeometry(w, d), new THREE.MeshStandardMaterial({ map: t, transparent, roughness: 0.55, depthWrite: !transparent }));
  m.userData.noPick = false;
  return m;
}

/** Chip DIP soldado en un PCB (y0 = cara superior); along = 'x' o 'z' (eje largo). */
export function dipOnBoard(root, b, n, cx, y0, cz, along, label, sub = '') {
  const half = n / 2;
  const len = half * P - 0.4;
  const [w, d] = along === 'x' ? [len, 6.35] : [6.35, len];
  b.box(w, 3.3, d, M.chip, cx, y0 + 0.5, cz);
  for (let i = 0; i < half; i++) {
    const o = (i - (half - 1) / 2) * P;
    for (const s of [-1, 1]) {
      const [x, z] = along === 'x' ? [cx + o, cz + s * 3.81] : [cx + s * 3.81, cz + o];
      const [lx, lz] = along === 'x' ? [0.5, 1.2] : [1.2, 0.5];
      b.box(lx, 0.25, lz, M.tin, along === 'x' ? x : cx + s * 3.45, y0 + 1.8, along === 'x' ? cz + s * 3.45 : z);
      b.box(along === 'x' ? 0.5 : 0.3, 2.0, along === 'x' ? 0.3 : 0.5, M.tin, x, y0, z);
    }
  }
  const lw = along === 'x' ? len : 6.35;
  const ld = along === 'x' ? 6.35 : len;
  const top = labelPlane(lw, ld, `dip-${n}-${along}-${label}-${sub}`, (g, W, D) => {
    g.fillStyle = '#1b1b1e';
    g.fillRect(0, 0, W, D);
    g.save();
    if (along === 'z') {
      g.translate(W, 0);
      g.rotate(Math.PI / 2);
    }
    const L = along === 'x' ? W : D;
    const H = along === 'x' ? D : W;
    g.fillStyle = '#101012';
    g.beginPath();
    g.arc(0, H / 2, 0.9, -Math.PI / 2, Math.PI / 2);
    g.fill();
    g.beginPath();
    g.arc(1.5, H - 1.3, 0.4, 0, Math.PI * 2);
    g.fill();
    text(g, label, L / 2, H / 2 - (sub ? 0.75 : 0), { size: 1.35, color: '#a8aaae', weight: 600 });
    if (sub) text(g, sub, L / 2, H / 2 + 1.1, { size: 0.95, color: '#8a8c90', weight: 500 });
    g.restore();
  }, { transparent: false });
  top.rotation.x = -Math.PI / 2;
  top.position.set(cx, y0 + 3.82, cz);
  root.add(top);
}

/** Capacitor electrolítico vertical con franja del negativo. */
export function electrolytic(root, x, y0, z, r, h, { color = '#1d2a4a', stripe = '#c9ced6' } = {}) {
  const key = `ecap-${color}-${stripe}`;
  const tex = sharedTexture(key, 64, 16, (g) => {
    g.fillStyle = color;
    g.fillRect(0, 0, 64, 16);
    g.fillStyle = stripe;
    g.fillRect(44, 0, 10, 16);
    g.fillStyle = color;
    for (let i = 0; i < 4; i++) g.fillRect(48, 1.5 + i * 3.8, 2, 1.6);
  }, 4);
  const side = new THREE.MeshStandardMaterial({ map: tex, roughness: 0.45 });
  const topM = mat('#b9bec6', { roughness: 0.35, metalness: 0.6 });
  const mesh = new THREE.Mesh(sharedGeo(`ecap${r},${h}`, () => new THREE.CylinderGeometry(r, r, h, 24)), [side, topM, mat('#20222a', { roughness: 0.6 })]);
  mesh.position.set(x, y0 + h / 2 + 0.3, z);
  mesh.castShadow = true;
  root.add(mesh);
  return mesh;
}

/** Componente SMD (resistencia o capacitor) pequeño. */
export function smd(b, x, y0, z, kind = 'r', rot = 0) {
  if (kind === 'r') {
    b.box(2.0, 0.5, 1.25, mat('#17181a', { roughness: 0.5 }), x, y0, z, { ry: rot });
    b.box(0.35, 0.52, 1.25, M.tin, x + Math.cos(rot) * 0.85, y0, z - Math.sin(rot) * 0.85, { ry: rot });
    b.box(0.35, 0.52, 1.25, M.tin, x - Math.cos(rot) * 0.85, y0, z + Math.sin(rot) * 0.85, { ry: rot });
  } else {
    b.box(2.0, 0.8, 1.25, mat('#b59a6e', { roughness: 0.6 }), x, y0, z, { ry: rot });
  }
}

/** Cable flexible a lo largo de puntos [x, y, z]. */
export function cable(b, pts, r, color, seg = 32) {
  const curve = new THREE.CatmullRomCurve3(pts.map((p) => new THREE.Vector3(...p)), false, 'centripetal', 0.5);
  b.add(new THREE.TubeGeometry(curve, seg, r, 8, false), mat(color, { roughness: 0.45 }));
}

/** Punta de cable estañada que termina en (x, y, z) apuntando hacia dir (±1 en X). */
export function tinnedTip(b, x, y, z, dir = 1, len = 3.2) {
  b.cylX(0.36, len, M.tin, x - (dir * len) / 2, y, z, 10);
}
